import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ToastMessage from '../../component/ToastMessage';
import { useMemberCustomerMutation } from '../../queries/Imitate';
import { useDispatch, useSelector } from 'react-redux';
import './style.scss';
import Button from '../../component/Button';
import { logIn, startImpersonation } from '../../store/userSlice';
import { useCreateCustomerFromTokenMutation } from '../../queries/CreateCustomer';
import Cookies from 'js-cookie';
import { RootState } from '../../store/store';

const ImitateCustomer: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userData, setUserData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('');
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const auth = Cookies.get('ck-customer');
  const userIsLoggedIn = isLoggedIn || !!auth;

  const dispatchUserData = (data: any) => {
    dispatch(
      startImpersonation({
        id: data.id,
        email: data.email,
        firstName: data.first_name,
        lastName: data.last_name,
        city: data.city,
        phone: data.phone,
        state_address: data.state,
        zip: data.zipcode,
        street_address: data.street_address,
        birthday: data.birthday,
        __typename: data.__typename,
        fcm_token: data.fcm_token || null,
        exp: data.exp
      })
    );
  };

  const handleSuccess = (data: any) => {
    setLoading(false);
    setStatus(data?.status);

    const user = data?.userData || data;

    if (data?.status === 'success' && user && Object.keys(user).length > 0) {
      setUserData(user);
      dispatch(
        logIn({
          id: user.id,
          email: user.email,
          firstName: user.first_name,
          lastName: user.last_name,
          exp: user.exp
        })
      );
      dispatchUserData(user);
      navigate('/my-profile');
    }

    ToastMessage({
      flag: data.status.charAt(0).toUpperCase() + data.status.slice(1),
      message: data.message
    });
  };

  const handleError = (error: any) => {
    setLoading(false);
    setStatus('error');
    ToastMessage({ flag: 'Error', message: 'Failed to load user data' });
  };

  const handleCreateSuccess = (data: any) => {
    setLoading(false);
    setStatus(data?.status);

    const user = data?.userData || data;

    if (data?.status === 'success' && user && Object.keys(user).length > 0) {
      setUserData(user);

      dispatch(
        logIn({
          id: user.id,
          email: user.email,
          firstName: user.first_name,
          lastName: user.last_name,
          exp: user.exp
        })
      );
      dispatchUserData(user);
      navigate('/my-profile');
    }

    ToastMessage({
      flag: data.status.charAt(0).toUpperCase() + data.status.slice(1),
      message: data.message
    });
  };

  const { mutate: memberCustomer } = useMemberCustomerMutation(handleSuccess, handleError);

  const { mutate: createCustomerFromToken } = useCreateCustomerFromTokenMutation(
    handleCreateSuccess,
    handleError
  );

  useEffect(() => {
    if (token) {
      setLoading(true);
      memberCustomer({ jwtToken: token });
    } else {
      ToastMessage({ flag: 'Error', message: 'Missing token' });
      navigate('/');
    }
  }, []);

  const handleCreateAccount = () => {
    if (token) {
      setLoading(true);
      createCustomerFromToken({ jwtToken: token });
    }
  };

  return (
    <div className="imitate-customer-page">
      <h2>Imitate Customer</h2>

      {loading && <p>Loading user data...</p>}

      {!loading && userData?.email && (
        <div className="user-details">
          <p>
            <strong>Email:</strong> {userData.email}
          </p>
          <p>
            <strong>First Name:</strong> {userData.first_name}
          </p>
          <p>
            <strong>Last Name:</strong> {userData.last_name}
          </p>
          <p>
            <strong>Street Address:</strong> {userData.street_address}
          </p>
          <p>
            <strong>City:</strong> {userData.city}
          </p>
          <p>
            <strong>State:</strong> {userData.state}
          </p>
          <p>
            <strong>Zipcode:</strong> {userData.zipcode}
          </p>
          <p>
            <strong>Phone:</strong> {userData.phone}
          </p>
          <p>
            <strong>Birthday:</strong> {userData.birthday}
          </p>
        </div>
      )}

      {!loading && status === 'error' && (
        <div className="no-user-message">
          <p>
            The user you are trying to access does not exist. Would you like to create an account
            for them?
          </p>
          <div className="btn-container">
            <Button
              type="submit"
              disabled={!userIsLoggedIn}
              className={!userIsLoggedIn ? 'disabled-button' : ''}
              onClick={handleCreateAccount}
            >
              Create Account
            </Button>
            {!userIsLoggedIn && (
              <span className="tooltip-message">Please log in to view these features</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImitateCustomer;
